import React, { ComponentProps } from 'react';
import { cx } from 'class-variance-authority';
import { Dialog as BaseUIDialog } from '@base-ui/react/dialog';
import Icon from '@ui/Icon';
import styles from './modal.module.css';
import {
  variants,
  padVariants,
  type ModalVariants,
  type PadVariants,
} from './modal-variants';

export const ModalTrigger = BaseUIDialog.Trigger;

export type ModalPopupProps = ComponentProps<typeof BaseUIDialog.Popup>;

export type ModalCloseProps = ComponentProps<typeof BaseUIDialog.Close>;
export const ModalClose = ({ className, ...rest }: ModalCloseProps) => (
  <BaseUIDialog.Close {...rest} className={cx(className, styles.close)} />
);

type ModalProps = PadVariants & {
  children: React.ReactNode;
  dismissable?: boolean;
  open?: boolean;
  close: () => void;
  id: string;
  'data-e2e': string;
  title?: React.ReactNode;
  banner?: boolean;
  size?: NonNullable<ModalVariants['size']>;
};

const CloseIcon = (
  <Icon type="close" fontSize="large" htmlColor="var(--text-primary)" />
);

const BaseDismissable = (
  <ModalClose className={styles['close-button']} aria-label="Close">
    {CloseIcon}
  </ModalClose>
);

const BannerDismissable = (
  <ModalClose aria-label="Close">{CloseIcon}</ModalClose>
);

export const ModalRoot = BaseUIDialog.Root;

/**
 * The modal surface. Renders Base UI's `Portal` + `Backdrop` internally so
 * consumers only place content inside `ModalPopup` — no manual plumbing.
 */
export const ModalPopup = React.forwardRef<HTMLDivElement, ModalPopupProps>(
  function ModalPopup({ id, className, ...rest }, ref) {
    return (
      <BaseUIDialog.Portal>
        <BaseUIDialog.Backdrop className={styles.backdrop} />
        <BaseUIDialog.Popup
          ref={ref}
          id={id}
          data-e2e={id}
          {...rest}
          className={cx(className, styles.popup)}
        />
      </BaseUIDialog.Portal>
    );
  }
);
ModalPopup.displayName = 'ModalPopup';

const ModalBanner = ({
  title,
  dismissable,
}: Pick<ModalProps, 'title' | 'dismissable'>) => (
  <div className={styles.banner}>
    <BaseUIDialog.Title render={<div />}>{title}</BaseUIDialog.Title>
    {dismissable && BannerDismissable}
  </div>
);

const ModalHeader = ({
  title,
  dismissable,
}: Pick<ModalProps, 'title' | 'dismissable'>) => (
  <div className={styles.header}>
    {dismissable && BaseDismissable}
    <BaseUIDialog.Title render={<div className={styles['header-title']} />}>
      {title}
    </BaseUIDialog.Title>
  </div>
);

const ModalTop = ({
  title,
  banner,
  dismissable,
}: Pick<ModalProps, 'title' | 'banner' | 'dismissable'>) => {
  if (!title) return null;
  if (banner) {
    return <ModalBanner title={title} dismissable={dismissable} />;
  }
  return <ModalHeader title={title} dismissable={dismissable} />;
};

export const Modal = ({
  children,
  open = true,
  dismissable = true,
  close,
  id,
  'data-e2e': dataE2E,
  title,
  banner,
  size,
  pad,
}: ModalProps) => {
  return (
    <ModalRoot
      open={open}
      disablePointerDismissal={!dismissable}
      onOpenChange={(nextOpen, eventDetails) => {
        if (nextOpen) return;
        if (!dismissable && eventDetails.reason === 'escape-key') {
          eventDetails.cancel();
          return;
        }
        close();
      }}
    >
      <ModalPopup id={id} data-e2e={dataE2E} className={variants({ size })}>
        <ModalTop title={title} banner={banner} dismissable={dismissable} />
        <div className={padVariants({ pad })}>{children}</div>
      </ModalPopup>
    </ModalRoot>
  );
};
